import { useEffect, useState } from "react";
import { Mail, RefreshCw, CheckCircle2, XCircle, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import EmailAuditModal from "../../components/EmailAuditModal";

/**
 * Auditoria de emails — leitura da coleção `db.notifications` filtrada por
 * `kind` começando com "email.". Útil para diagnosticar entregas falhadas,
 * emails que o cliente afirma não ter recebido, e para ver o que cada
 * fluxo dispara em produção.
 */
export default function AdminEmailAudit() {
  const [data, setData] = useState({ items: [], kinds: [], totals: {} });
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/admin/email-audit", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, status]);

  const totals = data.totals || {};
  const items = data.items || [];

  return (
    <div className="space-y-6" data-testid="admin-email-audit">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-[#d4af37] flex items-center gap-2">
            <Mail size={14} /> Admin · Diagnóstico
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Auditoria de emails</h1>
          <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
            Histórico de todos os emails enviados pela plataforma (Resend).
            Útil para confirmar que um cliente recebeu uma confirmação ou
            recuperação de password.
          </p>
        </div>
        <button onClick={load} disabled={loading}
                className="p-2 border border-[#1a4640] text-[#a0b2af] hover:border-[#d4af37] hover:text-[#d4af37] disabled:opacity-50"
                data-testid="email-audit-refresh">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
        </button>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="email-audit-kpis">
        <Kpi label="Devolvidos nesta vista" value={totals.returned ?? 0} />
        <Kpi label="Enviados (24h)" value={totals.sent_24h ?? 0} icon={<CheckCircle2 size={12} />} color="#10b981" />
        <Kpi label="Falhados (24h)" value={totals.failed_24h ?? 0} icon={<XCircle size={12} />}
             color={(totals.failed_24h || 0) > 0 ? "#ef4444" : "#a0b2af"} />
      </section>

      {/* Filters */}
      <section className="lux-card p-4 grid md:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Tipo</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)}
                  className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none mt-1"
                  data-testid="email-audit-kind">
            <option value="">Todos</option>
            {data.kinds.map(k => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Estado</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none mt-1"
                  data-testid="email-audit-status">
            <option value="">Todos</option>
            <option value="sent">Enviado</option>
            <option value="failed">Falhado</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Pesquisar destinatário ou assunto</label>
          <form onSubmit={(e) => { e.preventDefault(); load(); }}
                className="flex gap-2 mt-1">
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="ex.: cliente@exemplo.pt"
                   className="flex-1 bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"
                   data-testid="email-audit-search" />
            <button type="submit"
                    className="px-3 py-2 border border-[#d4af37] text-[#d4af37] text-xs uppercase tracking-widest hover:bg-[#d4af37]/10"
                    data-testid="email-audit-search-btn">
              <Search size={12} />
            </button>
          </form>
        </div>
      </section>

      {/* Table */}
      {loading ? (
        <div className="text-sm text-[#a0b2af]">A carregar…</div>
      ) : items.length === 0 ? (
        <div className="lux-card p-10 text-center text-sm text-[#a0b2af]"
             data-testid="email-audit-empty">
          Sem registos para os filtros aplicados.
        </div>
      ) : (
        <>
          {/* Mobile — stacked cards (default; hidden ≥md) */}
          <div className="md:hidden space-y-3" data-testid="email-audit-cards">
            {items.map(r => (
              <article key={r.id}
                       onClick={() => setOpenId(r.id)}
                       className="lux-card p-4 space-y-2 cursor-pointer hover:border-[#d4af37]/60 transition-colors"
                       data-testid={`email-row-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] font-mono">
                      {new Date(r.created_at).toLocaleString("pt-PT", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </div>
                    <div className="text-[11px] text-[#d4af37] font-mono mt-0.5 break-all">
                      {(r.kind || "").replace("email.", "")}
                    </div>
                  </div>
                  {r.status === "sent" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#10b981] shrink-0">
                      <CheckCircle2 size={10} /> Enviado
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#fca5a5] shrink-0"
                          title={r.error || ""}>
                      <XCircle size={10} /> Falhado
                    </span>
                  )}
                </div>
                <div className="text-sm text-[#fdfbf7] break-all">{r.to}</div>
                <div className="text-xs text-[#cde3df] leading-snug">{r.subject}</div>
                {r.error && r.status === "failed" && (
                  <div className="text-[10px] text-[#fca5a5] italic break-all border-t border-[#1a4640] pt-2 mt-2">
                    {r.error}
                  </div>
                )}
              </article>
            ))}
          </div>

          {/* Desktop — full table (≥md) */}
          <div className="hidden md:block lux-card overflow-x-auto" data-testid="email-audit-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-[10px] uppercase tracking-widest text-[#a0b2af] bg-[#062c27]">
                <tr>
                  <th className="text-left px-4 py-3">Quando</th>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Para</th>
                  <th className="text-left px-4 py-3">Assunto</th>
                  <th className="text-left px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id} className="border-t border-[#1a4640] hover:bg-[#062c27]/50 cursor-pointer"
                      onClick={() => setOpenId(r.id)}
                      data-testid={`email-row-desktop-${r.id}`}>
                    <td className="px-4 py-3 text-[11px] text-[#a0b2af] font-mono whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString("pt-PT", {
                        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-3 text-[11px]">
                      <span className="text-[#d4af37] font-mono">{(r.kind || "").replace("email.", "")}</span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-[#fdfbf7]">{r.to}</td>
                    <td className="px-4 py-3 text-[12px] text-[#cde3df] truncate max-w-[40ch]" title={r.subject}>{r.subject}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.status === "sent" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#10b981]">
                          <CheckCircle2 size={10} /> Enviado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#fca5a5]"
                              title={r.error || ""}>
                          <XCircle size={10} /> Falhado
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {openId && (
        <EmailAuditModal notifId={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

function Kpi({ label, value, icon, color }) {
  return (
    <div className="lux-card p-4">
      <div className="eyebrow text-[10px] flex items-center gap-1.5 text-[#a0b2af]">
        {icon} {label}
      </div>
      <div className="font-display text-3xl mt-1" style={{ color: color || "#fdfbf7" }}>{value}</div>
    </div>
  );
}
